import {
  ANCHOR_TYPE,
  INSTRUMENT_EVENT,
  OFFSET_DIRECTION,
  OFFSET_UNIT,
  RESOLVE_TO,
  START_OR_END,
  VISIT_STATUS,
  AGE_BASIS,
  TARGETING_ATTRIBUTE,
  TARGETING_OPERATOR,
  SAVE_RESUME_MODE,
  EDIT_AFTER_SUBMISSION_MODE,
  PLATFORM_TYPE,
  COHORT_TYPE,
  audienceTypeToCompletedBy,
} from '../constants'
import { createEmptySchedule } from '../models/createEmptySchedule'
import { createEmptyAnchor } from '../models/createEmptyAnchor'
import { createEmptyTargetingRow } from '../models/createEmptyTargetingRow'
import { createLocalId } from '../models/ids'
import { mapRepeatFromApi } from './mapRepeatToApi'
import { REPEAT_MODE } from '../constants/repeatTypes'
import {
  mapFixedDateFromApi,
  mapLegacyAvailability,
  mapLegacyEndRules,
  mapLegacyRepeat,
  mapPlatformFromLegacy,
} from './legacySchedulePayload'

const asArray = value => (Array.isArray(value) ? value : value ? [value] : [])

const mapVisitPoint = point => {
  if (point === 'target_date' || point === 'target') return VISIT_STATUS.targetDate
  if (point === 'completed' || point === 'completion') return VISIT_STATUS.completedDate
  if (point === 'missed') return VISIT_STATUS.missedDate
  if (point === 'appointment_scheduled') return VISIT_STATUS.appointmentScheduledDate
  return point || VISIT_STATUS.targetDate
}

const mapInstrumentPoint = point => {
  if (point === 'available' || point === 'start') return INSTRUMENT_EVENT.available
  if (point === 'completed' || point === 'completion' || point === 'end') return INSTRUMENT_EVENT.completion
  if (point === 'missed') return INSTRUMENT_EVENT.missed
  return point || INSTRUMENT_EVENT.available
}

const mapOffsetFromApi = offset => ({
  amount: offset?.value ?? offset?.amount ?? 0,
  unit: offset?.unit || OFFSET_UNIT.days,
  direction: offset?.direction || OFFSET_DIRECTION.after,
})

const mapAnchorTypeFromApi = type => {
  if (type === 'participant_start' || type === ANCHOR_TYPE.participantStartDate) return ANCHOR_TYPE.participantStartDate
  if (type === 'participant_first_login' || type === ANCHOR_TYPE.participantLogin) return ANCHOR_TYPE.participantLogin
  if (type === 'absolute' || type === 'exact_date' || type === ANCHOR_TYPE.fixedCalendarDate) {
    return ANCHOR_TYPE.fixedCalendarDate
  }
  return type || ANCHOR_TYPE.participantStartDate
}

const mapPlatformFromApi = platformType => mapPlatformFromLegacy(platformType)

const mapAnchorFromApi = apiAnchor => {
  const base = createEmptyAnchor({ id: createLocalId('anchor') })
  const type = mapAnchorTypeFromApi(apiAnchor?.type)
  const visitRefs = asArray(apiAnchor?.visits).map(visit => ({
    visitId: String(visit.visit_id || visit.id || ''),
    armId: String(visit.arm_id || visit.armId || ''),
    label: visit.label || '',
  }))
  const instanceIds = visitRefs.map(ref => ref.visitId).filter(Boolean)
  const instanceId = String(
    apiAnchor?.cycle_id ||
      apiAnchor?.arm_id ||
      apiAnchor?.phase_id ||
      apiAnchor?.track_id ||
      apiAnchor?.chapter_id ||
      apiAnchor?.instrument_id ||
      apiAnchor?.instance_id ||
      instanceIds[0] ||
      '',
  )

  return {
    ...base,
    type,
    instanceId,
    instanceIds,
    visitRefs,
    startOrEnd: apiAnchor?.point === 'end' ? START_OR_END.end : START_OR_END.start,
    visitStatus: type === ANCHOR_TYPE.visit ? mapVisitPoint(apiAnchor?.point) : '',
    instrumentEvent: type === ANCHOR_TYPE.instrument ? mapInstrumentPoint(apiAnchor?.point) : '',
    offset: mapOffsetFromApi(apiAnchor?.offset),
    timingMode: apiAnchor?.timing_mode || base.timingMode,
    fixedDate: mapFixedDateFromApi(apiAnchor),
  }
}

export const normalizeCohortEntries = cohort => {
  if (!cohort) return []
  if (Array.isArray(cohort)) return cohort
  if (typeof cohort === 'object' && cohort.type) return [cohort]
  return []
}

export const mapCohortToTargeting = (cohortInput = []) => {
  const cohort = normalizeCohortEntries(cohortInput)
  return cohort
    .flatMap(entry => {
      const type = entry?.type
      if (
        type === COHORT_TYPE.allParticipants ||
        type === 'all_participants' ||
        type === COHORT_TYPE.allUsers ||
        type === 'all_users' ||
        type === COHORT_TYPE.allCaregivers ||
        type === 'all_caregivers'
      ) {
        return []
      }
      if (type === COHORT_TYPE.age || type === 'age') {
        const filter = entry.filter || {}
        return [
          createEmptyTargetingRow({
            attribute: TARGETING_ATTRIBUTE.age,
            operator: TARGETING_OPERATOR.gte,
            value: String(filter.min_age ?? filter.value ?? ''),
            maxAge: filter.max_age,
            ageBasis: filter.basis === 'current' ? AGE_BASIS.current : AGE_BASIS.atEnrollment,
          }),
        ]
      }
      // Legacy schedules may store arm/phase in cohort; deploy queue only accepts audience filters.
      // Pinning is restored from deploy anchors via applyAnchorLocks.
      if (
        type === COHORT_TYPE.arm ||
        type === 'arm' ||
        type === COHORT_TYPE.periodPhase ||
        type === 'period_phase' ||
        type === 'phase'
      ) {
        return []
      }
      if (type === COHORT_TYPE.sites || type === 'sites' || type === COHORT_TYPE.siteLocation || type === 'site_location') {
        const ids = entry.filter?.site_ids || [entry.filter?.site_id || entry.filter?.value]
        return ids.filter(Boolean).map(id =>
          createEmptyTargetingRow({
            attribute: TARGETING_ATTRIBUTE.siteLocation,
            operator: TARGETING_OPERATOR.equals,
            value: String(id),
          }),
        )
      }
      if (type === COHORT_TYPE.language || type === 'language') {
        return [
          createEmptyTargetingRow({
            attribute: TARGETING_ATTRIBUTE.language,
            operator: TARGETING_OPERATOR.equals,
            value: String(entry.filter?.language || entry.filter?.value || ''),
          }),
        ]
      }
      return []
    })
    .filter(Boolean)
}

const mapPermissionsFromApi = permissions => {
  const save = permissions?.save_and_resume || permissions?.saveResume || {}
  const edit = permissions?.edit_after_submission || permissions?.editAfterSubmission || {}
  return {
    saveResume: {
      mode: save.enabled ? SAVE_RESUME_MODE.yesMinutes : SAVE_RESUME_MODE.no,
      value: save.minutes ?? save.within_minutes ?? 30,
      unit: save.unit || 'minutes',
    },
    editAfterSubmission: {
      mode: !edit.enabled
        ? EDIT_AFTER_SUBMISSION_MODE.no
        : edit.until_expires
          ? EDIT_AFTER_SUBMISSION_MODE.untilExpires
          : edit.no_limit
            ? EDIT_AFTER_SUBMISSION_MODE.noLimit
            : EDIT_AFTER_SUBMISSION_MODE.hours,
      value: edit.hours ?? edit.within_hours ?? 24,
      unit: edit.unit || 'hours',
    },
  }
}

const mapContractAvailability = (availability = {}, baseAvailability) => {
  const daily = availability?.daily_window || {}
  return {
    ...baseAvailability,
    mode: availability?.mode || baseAvailability.mode,
    openFor: {
      value: availability?.open_for?.value ?? baseAvailability.openFor.value,
      unit: availability?.open_for?.unit || baseAvailability.openFor.unit,
    },
    dailyWindow: {
      enabled:
        daily?.enabled != null
          ? !!daily.enabled
          : !!(daily?.start_local || daily?.from) && !!(daily?.end_local || daily?.until),
      from: daily?.start_local || daily?.from || baseAvailability.dailyWindow.from,
      until: daily?.end_local || daily?.until || baseAvailability.dailyWindow.until,
      timezone: daily?.timezone || baseAvailability.dailyWindow.timezone,
    },
  }
}

const normalizeByArmFromApi = byArm => {
  const source = byArm || {}
  return Object.fromEntries(
    Object.entries(source).map(([armKey, entry]) => [
      String(armKey),
      {
        visit_id: String(entry?.visit_id || entry?.visitId || ''),
      },
    ]),
  )
}

const resolveCompletedByFromApi = (apiEntry, audience) => {
  if (audience?.completed_by) return audience.completed_by
  const cohortAudienceType = asArray(audience?.cohort || apiEntry?.cohort).find(entry =>
    [COHORT_TYPE.allParticipants, COHORT_TYPE.allCaregivers, COHORT_TYPE.allUsers, 'all_participants', 'all_caregivers', 'all_users'].includes(
      entry?.type,
    ),
  )?.type
  return audienceTypeToCompletedBy(apiEntry?.audience_type || cohortAudienceType)
}

const mapContractEndRules = (endRules = {}, baseEndRules) => ({
  combine: endRules?.combine || baseEndRules.combine,
  rules: asArray(endRules?.rules).map(rule => ({
    id: createLocalId('end_rule'),
    type: rule?.type || '',
    offset: mapOffsetFromApi(rule?.offset),
    count: rule?.count ?? 12,
    byArm: normalizeByArmFromApi(rule?.by_arm || rule?.byArm),
  })),
})

const hasContractEndRules = apiEntry => asArray(apiEntry?.end_rules?.rules).some(rule => rule?.type)

/** Repeat is stored on `repeat` (contract) or legacy `recurring`; legacy `expire` alone must not clear repeat when end_rules exist. */
const resolveRepeatFromApi = (apiEntry, baseRepeat) => {
  const explicitRepeatMode = apiEntry?.repeat?.mode

  if (apiEntry?.repeat != null && explicitRepeatMode) {
    const contractRepeat = mapRepeatFromApi(apiEntry.repeat, baseRepeat)
    if (contractRepeat.mode === REPEAT_MODE.doesNotRepeat) {
      return { ...baseRepeat, ...contractRepeat, mode: REPEAT_MODE.doesNotRepeat }
    }
    if (contractRepeat.mode === REPEAT_MODE.oncePerAnchorOccurrence) {
      return { ...baseRepeat, ...contractRepeat, mode: REPEAT_MODE.oncePerAnchorOccurrence }
    }
    if (contractRepeat.mode && contractRepeat.mode !== REPEAT_MODE.doesNotRepeat) {
      return contractRepeat
    }
  }

  if (
    explicitRepeatMode === REPEAT_MODE.doesNotRepeat ||
    explicitRepeatMode === REPEAT_MODE.oncePerAnchorOccurrence
  ) {
    return { ...baseRepeat, mode: explicitRepeatMode }
  }

  if (apiEntry?.recurring != null) {
    const legacyRepeat = mapLegacyRepeat(apiEntry, baseRepeat)
    if (legacyRepeat.mode !== REPEAT_MODE.doesNotRepeat) return legacyRepeat
  }
  if (hasContractEndRules(apiEntry)) {
    const recurring = apiEntry?.recurring || {}
    return {
      ...baseRepeat,
      mode: REPEAT_MODE.everyNDays,
      intervalDays: Number(recurring.interval) || baseRepeat.intervalDays || 1,
    }
  }
  return mapRepeatFromApi(apiEntry?.repeat || {}, baseRepeat)
}

export const fromApiSchedule = apiEntry => {
  const base = createEmptySchedule()
  const deploy = apiEntry?.deploy || {}
  const combine = deploy?.combine
  const anchors = asArray(deploy?.anchors).map(mapAnchorFromApi)
  const audience = apiEntry?.audience || {}
  const platformType = mapPlatformFromApi(audience.platform_type || apiEntry?.platform_type)
  const usesLegacyAvailability =
    apiEntry?.availability == null &&
    (apiEntry?.recurring != null ||
      apiEntry?.expire != null ||
      (apiEntry?.cohort != null && audience?.cohort == null && !apiEntry?.repeat))
  const usesLegacyEndRulesFallback =
    apiEntry?.recurring != null ||
    apiEntry?.expire != null ||
    (apiEntry?.cohort != null && audience?.cohort == null && !apiEntry?.repeat)

  const availability = usesLegacyAvailability
    ? mapLegacyAvailability(apiEntry, base.availability)
    : mapContractAvailability(apiEntry?.availability, base.availability)
  const repeat = resolveRepeatFromApi(apiEntry, base.repeat)
  const contractEndRules = mapContractEndRules(apiEntry?.end_rules, base.endRules)
  let endRules = contractEndRules
  if (repeat.mode === REPEAT_MODE.doesNotRepeat) {
    endRules = { ...contractEndRules, rules: [] }
  } else if (!contractEndRules.rules?.length && usesLegacyEndRulesFallback) {
    endRules = mapLegacyEndRules(apiEntry, base.endRules)
  }

  return {
    ...base,
    id: apiEntry?.schedule_id || createLocalId('schedule'),
    scheduleId: apiEntry?.schedule_id || base.scheduleId,
    version: apiEntry?.version || base.version,
    name: apiEntry?.name || '',
    label: apiEntry?.name || '',
    combineAnchors: combine === RESOLVE_TO.earliestOf || combine === RESOLVE_TO.latestOf,
    resolveTo: combine === RESOLVE_TO.earliestOf ? RESOLVE_TO.earliestOf : RESOLVE_TO.latestOf,
    anchors: anchors.length ? anchors : base.anchors,
    availability,
    repeat,
    timeOfDay: deploy?.time_of_day || base.timeOfDay,
    endRules,
    audience: {
      completedBy: resolveCompletedByFromApi(apiEntry, audience),
      platformType,
      inheritedFromAnchor: {
        arm: !!audience?.inherited_from_anchor?.arm,
        phase: !!audience?.inherited_from_anchor?.phase,
      },
    },
    instancePermissions: mapPermissionsFromApi(apiEntry?.instance_permissions),
    targeting: mapCohortToTargeting(audience?.cohort || apiEntry?.cohort || []),
    visibility: apiEntry?.visibility,
    visibilityRanges: apiEntry?.visibilityRanges,
  }
}

export const fromApiSchedules = payload => {
  const entries = asArray(payload)
  const schedules = entries.map(fromApiSchedule)
  return { schedules: schedules.length ? schedules : [createEmptySchedule()] }
}
